// La bàn HƯỚNG THEO KHÍ NGÀY: mặt la kinh 4 vành (độ · hướng · ngũ hành ·
// hợp/kỵ) xoay theo cảm biến điện thoại, kim cố định ở đỉnh chỉ hướng người dùng
// đang quay mặt tới. Máy không có cảm biến (desktop) thì kéo để xoay.
//
// Khác La bàn Bát Trạch: lớp luận ở đây theo NGŨ HÀNH CHI NGÀY (đổi mỗi ngày,
// ai cũng như nhau), không theo cung phi của từng người.
import { Compass, Hand, LocateFixed, RotateCcw } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";

import { ChuCung } from "@/components/la-ban-shared";
import { C, GHI_CHU_TRANG_THAI, mauNenBien, toXY, vanhQuat } from "@/lib/la-ban-geometry";
import { useCompassHeading } from "@/hooks/use-compass-heading";
import { chuanHoaGoc, huongTuGoc, sonTuGoc } from "@/lib/bat-trach";
import { MUC_KHI_META, luanHuongKhiNgay, type NguHanh } from "@/lib/huong-khi-ngay";
import { cn } from "@/lib/utils";

// Bán kính từng vành, từ ngoài vào trong.
const R_DO_NGOAI = 198;
const R_DO_TRONG = 178;
const R_HUONG_NGOAI = 176;
const R_HUONG_TRONG = 132;
const R_HANH_NGOAI = 130;
const R_HANH_TRONG = 92;
const R_MUC_NGOAI = 90;
const R_MUC_TRONG = 58;

export function LaBanKhiNgay({ khiNgay, canCu }: { khiNgay: NguHanh; canCu?: string }) {
  const { heading, trangThai, hoTro, batLaBan, tatLaBan } = useCompassHeading();
  const [gocKeo, setGocKeo] = useState(0);
  const keo = useRef<{ gocBatDau: number; gocKeoBatDau: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const dungCamBien = heading !== null;
  const gocHienTai = chuanHoaGoc(dungCamBien ? heading : gocKeo);

  const huong = huongTuGoc(gocHienTai);
  const son = sonTuGoc(gocHienTai);

  const luan = useMemo(() => luanHuongKhiNgay(khiNgay), [khiNgay]);
  const luanTheoTen = useMemo(() => new Map(luan.map((l) => [l.ten, l])), [luan]);
  const huongDangNhin = luanTheoTen.get(huong)!;

  // ── Kéo để xoay (máy không có cảm biến) ────────────────────────────────────
  const gocConTro = useCallback((e: React.PointerEvent) => {
    const o = svgRef.current?.getBoundingClientRect();
    if (!o) return 0;
    const dx = e.clientX - (o.left + o.width / 2);
    const dy = e.clientY - (o.top + o.height / 2);
    return chuanHoaGoc((Math.atan2(dx, -dy) * 180) / Math.PI);
  }, []);

  const batDauKeo = (e: React.PointerEvent) => {
    if (dungCamBien) return;
    keo.current = { gocBatDau: gocConTro(e), gocKeoBatDau: gocKeo };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const dangKeo = (e: React.PointerEvent) => {
    if (!keo.current) return;
    const delta = gocConTro(e) - keo.current.gocBatDau;
    setGocKeo(chuanHoaGoc(keo.current.gocKeoBatDau - delta));
  };
  const ketThucKeo = () => {
    keo.current = null;
  };

  const ghiChu = GHI_CHU_TRANG_THAI[trangThai];

  return (
    <div className="min-w-0">
      {/* ── Mặt la bàn ── */}
      <div className="relative mx-auto aspect-square w-full max-w-[380px]">
        {/* Kim cố định ở đỉnh — chỉ hướng người dùng đang quay mặt tới. */}
        <div className="pointer-events-none absolute left-1/2 top-0 z-10 -translate-x-1/2">
          <div className="size-0 border-x-[7px] border-t-[14px] border-x-transparent border-t-primary" />
        </div>

        <svg
          ref={svgRef}
          viewBox="0 0 400 400"
          className={cn(
            "size-full select-none",
            !dungCamBien && "cursor-grab active:cursor-grabbing",
          )}
          onPointerDown={batDauKeo}
          onPointerMove={dangKeo}
          onPointerUp={ketThucKeo}
          onPointerCancel={ketThucKeo}
          role="img"
          aria-label={`La bàn hướng theo khí ngày hành ${khiNgay}. Đang nhìn hướng ${huong}, ${MUC_KHI_META[huongDangNhin.muc].nhan}.`}
        >
          {/* Mặt la bàn xoay ngược góc hiện tại để hướng đang nhìn luôn ở đỉnh. */}
          <g transform={`rotate(${-gocHienTai} ${C} ${C})`}>
            <circle cx={C} cy={C} r={R_DO_NGOAI} className="fill-card stroke-border" />

            {/* Vành độ: vạch 10° một, đậm hơn ở mỗi 90°. */}
            {Array.from({ length: 36 }, (_, i) => i * 10).map((goc) => {
              const chinh = goc % 90 === 0;
              const [x1, y1] = toXY(goc, R_DO_NGOAI);
              const [x2, y2] = toXY(goc, chinh ? R_DO_TRONG - 4 : R_DO_TRONG + 4);
              return (
                <line
                  key={goc}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  className={chinh ? "stroke-primary/70" : "stroke-border"}
                  strokeWidth={chinh ? 1.6 : 0.8}
                />
              );
            })}

            {luan.map((h) => {
              const meta = MUC_KHI_META[h.muc];
              const a0 = h.goc - 22.5;
              const a1 = h.goc + 22.5;
              return (
                <g key={h.ten}>
                  {/* Vành hướng — nền tô theo hợp / bình / kỵ */}
                  <path
                    d={vanhQuat(R_HUONG_NGOAI, R_HUONG_TRONG, a0, a1)}
                    style={{ fill: mauNenBien(meta.bien, meta.dam) }}
                    className="stroke-border/70"
                    strokeWidth={0.8}
                  />
                  <ChuCung
                    id={`khi-huong-${h.goc}`}
                    r={(R_HUONG_NGOAI + R_HUONG_TRONG) / 2}
                    goc={h.goc}
                    doRong={40}
                    className="fill-foreground text-[15px] font-semibold"
                  >
                    {h.ten}
                  </ChuCung>

                  {/* Vành ngũ hành phương vị */}
                  <path
                    d={vanhQuat(R_HANH_NGOAI, R_HANH_TRONG, a0, a1)}
                    className="fill-muted/40 stroke-border/70"
                    strokeWidth={0.8}
                  />
                  <ChuCung
                    id={`khi-hanh-${h.goc}`}
                    r={(R_HANH_NGOAI + R_HANH_TRONG) / 2}
                    goc={h.goc}
                    doRong={40}
                    className="fill-muted-foreground text-[13px]"
                  >
                    {h.hanh}
                  </ChuCung>

                  {/* Vành kết luận */}
                  <path
                    d={vanhQuat(R_MUC_NGOAI, R_MUC_TRONG, a0, a1)}
                    style={{ fill: mauNenBien(meta.bien, meta.dam + 0.08) }}
                    className="stroke-border/70"
                    strokeWidth={0.8}
                  />
                  <ChuCung
                    id={`khi-muc-${h.goc}`}
                    r={(R_MUC_NGOAI + R_MUC_TRONG) / 2}
                    goc={h.goc}
                    doRong={40}
                    className="text-[12px] font-semibold"
                    style={{ fill: `var(${meta.bien})` }}
                  >
                    {meta.nhan}
                  </ChuCung>
                </g>
              );
            })}

            {/* Lòng la bàn: hành khí ngày */}
            <circle cx={C} cy={C} r={R_MUC_TRONG - 2} className="fill-card stroke-border" />
            <text
              x={C}
              y={C - 12}
              textAnchor="middle"
              className="fill-muted-foreground text-[11px]"
            >
              Khí ngày
            </text>
            <text
              x={C}
              y={C + 12}
              textAnchor="middle"
              className="fill-primary font-display text-[22px] font-bold"
            >
              {khiNgay}
            </text>
          </g>
        </svg>
      </div>

      {/* ── Hướng đang nhìn ── */}
      <div className="mt-3 rounded-xl border border-border/70 bg-muted/30 px-3 py-2.5">
        <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-[11px] text-muted-foreground">Đang nhìn</span>
          <span className="font-display text-base font-bold text-foreground">
            {huong} · {son}
          </span>
          <span className="text-[11px] text-muted-foreground">{Math.round(gocHienTai)}°</span>
          <span className={cn("ml-auto text-sm font-bold", MUC_KHI_META[huongDangNhin.muc].mau)}>
            {MUC_KHI_META[huongDangNhin.muc].nhan}
          </span>
        </p>
        <p className="mt-1 text-[11px] leading-5 text-muted-foreground">{huongDangNhin.ly_do}</p>
      </div>

      {/* ── Chú giải + điều khiển cảm biến ── */}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        {(["hop", "binh", "ky"] as const).map((m) => (
          <span key={m} className="inline-flex items-center gap-1.5">
            <span className={cn("size-2.5 rounded-full", MUC_KHI_META[m].diem)} />
            {MUC_KHI_META[m].nhan}
          </span>
        ))}
        <span className="ml-auto inline-flex items-center gap-1.5">
          {dungCamBien ? (
            <>
              <LocateFixed className="size-3.5 text-primary" /> Kim đang theo cảm biến
            </>
          ) : (
            <>
              <Hand className="size-3.5" /> Kéo mặt la bàn để tra hướng
            </>
          )}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {hoTro && !dungCamBien ? (
          <button
            type="button"
            onClick={batLaBan}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground"
          >
            <Compass className="size-3.5" /> Bật la bàn
          </button>
        ) : null}
        {dungCamBien ? (
          <button
            type="button"
            onClick={tatLaBan}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs font-semibold text-muted-foreground"
          >
            <RotateCcw className="size-3.5" /> Tắt cảm biến
          </button>
        ) : null}
        {canCu ? <span className="text-[11px] text-muted-foreground">{canCu}</span> : null}
      </div>

      {ghiChu ? (
        <p className="mt-3 rounded-xl border border-border/70 bg-muted/40 px-3 py-2 text-[11px] leading-5 text-muted-foreground">
          {ghiChu}
        </p>
      ) : null}
    </div>
  );
}
